import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useUpdateMutation } from "../../api/genericHooks";
import { LanguageSelectorModalButton } from "../../components/Buttons/LanguageSelectorModalButton";
import { logo } from "../../images";

type AcceptInvitationInstructionData = {
  first_name?: string;
  last_name?: string;
  invitation_token: string;
  password: string;
  password_confirmation: string;
  consent: boolean;
};

export const AcceptInvitation = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();

  // Get the invitation_token from the query string
  const invitation_token = new URLSearchParams(search).get("invitation_token");

  // Query Methods
  const acceptInvitationInstructionsQuery = useUpdateMutation("/invitation");

  // Form Methods
  const schema = yup.object().shape({
    first_name: yup.string(),
    last_name: yup.string(),
    password: yup
      .string()
      .min(6, t("errors.passwordMinLength"))
      .required(t("errors.required")),
    password_confirmation: yup
      .string()
      .oneOf(
        [yup.ref("password"), undefined],
        t("acceptInvitation.passwordConfirmationMatch")
      )
      .required(t("errors.required")),
    invitation_token: yup.string().required(t("errors.required")),
    consent: yup
      .boolean()
      .oneOf([true], t("errors.required"))
      .required(t("errors.required")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptInvitationInstructionData>({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: "",
      last_name: "",
      password: "",
      password_confirmation: "",
      invitation_token: invitation_token || "",
      consent: false,
    },
  });

  const onSubmit: SubmitHandler<AcceptInvitationInstructionData> = (data) => {
    if (!invitation_token) {
      toast.error(t("acceptInvitation.missingToken"));
      return;
    }

    acceptInvitationInstructionsQuery
      .mutateAsync({
        user: {
          first_name: data.first_name,
          last_name: data.last_name,
          invitation_token: invitation_token,
          password: data.password,
          password_confirmation: data.password_confirmation,
          consent: data.consent,
        },
      })
      .then(() => {
        toast.success(t("acceptInvitation.invitationAccepted"));
        navigate("/");
      });
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-delicate-50">
        <div className="w-full justify-items-end bg-delicate-50 sm:block">
          <LanguageSelectorModalButton />
        </div>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            alt="Memoirs logo"
            src={logo.logoGrey}
            className="mx-auto h-20 w-auto"
          />
          <h2 className="mt-6 font-poppins text-center text-2xl/9 font-bold tracking-tight text-slate-100">
            {t("acceptInvitation.createPassword")}
          </h2>
        </div>
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="flex w-full space-x-6">
                <div>
                  <label
                    htmlFor="firstname"
                    className="block font-nunito text-sm/6 font-medium text-slate-900"
                  >
                    {t("signup.fields.firstName")}
                  </label>
                  <div className="mt-2">
                    <input
                      id="firstname"
                      type="text"
                      autoComplete="firstname"
                      className="block w-full font-nunito rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                      {...register("first_name", { required: true })}
                    />
                    {errors.first_name && (
                      <span className="text-rust-400 font-nunito">
                        {errors.first_name.message}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="lastname"
                    className="block text-sm/6 font-nunito font-medium text-slate-900"
                  >
                    {t("signup.fields.lastName")}
                  </label>
                  <div className="mt-2">
                    <input
                      id="lastname"
                      type="text"
                      autoComplete="lastname"
                      className="block w-full font-nunito rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                      {...register("last_name", { required: true })}
                    />
                    {errors.last_name && (
                      <span className="text-rust-400 font-nunito">
                        {errors.last_name.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block font-nunito text-sm/6 font-medium text-slate-900"
                >
                  {t("acceptInvitation.password")}
                </label>
                <div className="mt-2">
                  <input
                    id="password"
                    type="password"
                    autoComplete="password"
                    className="block font-nunito w-full rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                    {...register("password", { required: true })}
                  />
                  {errors.password && (
                    <span className="text-red-500 font-nunito">
                      {errors.password.message}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block font-nunito text-sm/6 font-medium text-slate-900"
                >
                  {t("acceptInvitation.password_confirmation")}
                </label>
                <div className="mt-2">
                  <input
                    id="password_confirmation"
                    type="password"
                    autoComplete="password_confirmation"
                    className="block font-nunito w-full rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                    {...register("password_confirmation", { required: true })}
                  />
                  {errors.password_confirmation && (
                    <span className="text-red-500 font-nunito">
                      {errors.password_confirmation.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex h-6 shrink-0 items-center">
                <div className="group grid size-4 grid-cols-1">
                  <input
                    id="consent"
                    type="checkbox"
                    {...register("consent", { required: true })}
                    aria-describedby="consent-description"
                    className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-delicate-600 checked:bg-delicate-600 indeterminate:border-delicate-600 indeterminate:bg-delicate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-delicate-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                  />
                  <svg
                    fill="none"
                    viewBox="0 0 14 14"
                    className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                  >
                    <path
                      d="M3 8L6 11L11 3.5"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="opacity-0 group-has-[:checked]:opacity-100"
                    />
                    <path
                      d="M3 7H11"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="opacity-0 group-has-[:indeterminate]:opacity-100"
                    />
                  </svg>
                </div>
                <div className="text-sm/6 ml-2">
                  <span id="comments-description" className="text-slate-500">
                    {t("signup.fields.consent")}
                    <a
                      href={import.meta.env.VITE_MEMOIRS_PRIVACY_URL}
                      className="font-semibold font-nunito text-rust-400 hover:text-rust-50"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("signup.fields.terms")}
                    </a>
                  </span>
                  <div>
                    {errors.consent && (
                      <span className="text-rust-400 font-nunito">
                        {t("errors.required")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  className="flex w-full font-nunito justify-center rounded bg-rust-400 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-rust-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust-400"
                >
                  {t("acceptInvitation.submit")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
